import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { 
  HomeIcon, 
  BuildingStorefrontIcon, 
  CurrencyDollarIcon, 
  ReceiptPercentIcon,
  ArchiveBoxIcon,
  CreditCardIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
  CalculatorIcon,
  ScaleIcon,
  ChatBubbleLeftRightIcon,
  BanknotesIcon,
  DocumentMagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const Navigation = () => {
  const { companyName, logoUrl } = useTenant();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  // Check user role
  const isComprasOnly = user?.role === 'compras';

  // Primera fila - Funciones principales
  const primaryNavigation = isComprasOnly 
    ? [
        // Solo mostrar Inventario para usuarios de compras
        { name: 'Inventario', href: '/inventory', icon: ArchiveBoxIcon },
      ]
    : [
        { name: 'Dashboard', href: '/', icon: HomeIcon },
        { name: 'Tiendas', href: '/stores', icon: BuildingStorefrontIcon },
        { name: 'Ventas', href: '/sales', icon: CurrencyDollarIcon },
        { name: 'Gastos', href: '/expenses', icon: ReceiptPercentIcon },
        { name: 'Inventario', href: '/inventory', icon: ArchiveBoxIcon },
        { name: 'Cuentas por Pagar', href: '/accounts-payable', icon: CreditCardIcon },
        { name: 'Cashea', href: '/cashea', icon: BanknotesIcon,
  DocumentMagnifyingGlassIcon },
      ];

  // Segunda fila - Análisis y configuración (no mostrar para compras)
  const secondaryNavigation = isComprasOnly 
    ? []
    : [
        { name: 'Inventario Inicial', href: '/initial-inventory', icon: ScaleIcon },
        { name: 'Control de Efectivo', href: '/cash-control', icon: CurrencyDollarIcon },
        { name: 'Tasa de Cambio', href: '/exchange-rate', icon: ArrowsRightLeftIcon },
        { name: 'Punto de Equilibrio', href: '/break-even', icon: CalculatorIcon },
        { name: 'Conciliación', href: '/bank-reconciliation', icon: DocumentMagnifyingGlassIcon },
        { name: 'Reportes', href: '/reports', icon: ChartBarIcon },
      ];

  // Add admin-only navigation items to secondary row
  if (isAdmin) {
    secondaryNavigation.push(
      { name: 'Usuarios', href: '/users', icon: UserGroupIcon },
      { name: 'Telegram', href: '/whatsapp-setup', icon: ChatBubbleLeftRightIcon }
    );
  }

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-4">
        {/* Header with Logo and User Info */}
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src={logoUrl || "/farmavida_logo.png"} 
              alt={companyName} 
              className="h-10 w-10 rounded"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div>
              <h1 className="text-xl font-bold text-blue-900">
                {companyName}
              </h1>
              <p className="text-xs text-gray-600">Gestión Financiera</p>
            </div>
          </div>

          {/* User Info and Logout */}
          <div className="flex items-center space-x-4">
            <div className="text-sm">
              <span className="text-gray-600">Bienvenido, </span>
              <span className="font-medium text-gray-900">{user?.full_name || user?.username}</span>
              {isAdmin && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Admin
                </span>
              )}
            </div>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Primary Navigation Row */}
        <div className="border-t border-gray-100">
          <div className="flex items-center justify-center py-3">
            <div className="flex items-baseline space-x-6">
              {primaryNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    } px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors duration-200`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Secondary Navigation Row */}
        <div className="border-t border-gray-100">
          <div className="flex items-center justify-center py-3">
            <div className="flex items-baseline space-x-6">
              {secondaryNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-green-100 text-green-700 border-b-2 border-green-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    } px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors duration-200`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile menu - Single column for mobile */}
        <div className="lg:hidden border-t border-gray-100">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <div className="mb-4">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Operaciones Principales
              </h3>
              <div className="mt-2 space-y-1">
                {primaryNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        isActive
                          ? 'bg-blue-100 text-blue-700 border-r-4 border-blue-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
                    >
                      <Icon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Análisis y Configuración
              </h3>
              <div className="mt-2 space-y-1">
                {secondaryNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        isActive
                          ? 'bg-green-100 text-green-700 border-r-4 border-green-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
                    >
                      <Icon className="text-gray-400 group-hover:text-gray-500 mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;